import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { clienteEObra, data, plural } from '../formato';
import { FaixaDeFiltros, Seletor, SeletorDeCliente, type OpcaoDeFiltro } from '../Filtros';
import { useSessao } from '../sessao';
import { NomeDaAmostra, daApi, nomeDaAmostra } from '../NomeDaAmostra';
import {
  AcaoBloqueada, Area, Aviso, Campo, Carregando, Dialogo, ErroDaApi, Selo,
  SegurarParaConfirmar, Vazio,
} from '../ui';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../TabelaComFicha';

/**
 * Descarte de amostras (onda 174, migração 0175).
 *
 * ── A pergunta desta tela ────────────────────────────────────────────
 *
 * «O que pode sair da estante hoje — e por que o resto ainda não pode?»
 *
 * A elegibilidade é APURADA pelo banco, nunca digitada (RN-181): quatro
 * condições — ensaios aprovados, laudo emitido, laudo disponibilizado
 * ao cliente, prazo de guarda da OS vencido — e a fila é a consequência
 * do estado dos registros. Faltando uma, a amostra não aparece como
 * «pronta»; aparece na prateleira que diz O QUE falta, para ninguém
 * precisar adivinhar por que aquela amostra não sai do depósito.
 *
 * A baixa é em lote, pelo mesmo motivo da guarda em lote (doc 48): o
 * gesto real é «estas doze saem hoje». Cada baixa grava data-hora do
 * servidor, usuário, dispositivo e destinação (RN-182); nada se apaga
 * (RN-183).
 */

type Prateleira = 'pronta' | 'retida' | 'entrega' | 'emissao' | 'contando';

interface Linha {
  id: string; codigo: string; status: string;
  tipo_amostra: string | null; tipo_acondicionamento: string | null;
  identificacao_recebida: string | null;
  os_id: string | null; os_numero: string | null;
  obra_codigo: string | null; obra_nome: string | null;
  cliente_id: string | null; cliente: string | null;
  estado_custodia: string; local_nome: string | null; onde_esta: string;
  datahora_recebimento: string | null; dias_no_laboratorio: number | null;
  ensaios: string | number; ensaios_na_conta: string | number;
  concluida: boolean; entregue: boolean;
  relatorio_aprovado_em: string | null;
  relatorio_disponibilizado_em: string | null;
  prazo_guarda_dias: number | null;
  descarte_a_partir_de: string | null;
  dias_para_descarte: number | null;
  retida: boolean; retencao_ate: string | null; retencao_motivo: string | null;
  retencao_pedido_por: string | null;
  descarte_autorizado: boolean; autorizacao_valida_ate: string | null;
  autorizacao_motivo: string | null;
  elegivel_descarte: boolean;
  impedimento_descarte: string | null; impedimento_texto: string | null;
  prateleira: Prateleira;
}

interface Resumo {
  prontas: number; retidas: number; aguardando_entrega: number;
  aguardando_emissao: number; contando: number; vencem_em_7_dias: number;
  autorizadas: number;
}

interface ResultadoBaixa {
  descartadas: number; recusadas: number;
  detalhe: {
    descartadas: { id: string; codigo: string }[];
    recusadas: { id: string; codigo: string | null; porque: string }[];
  };
}

/** As prateleiras, na ordem em que a tela as mostra. */
const PRATELEIRAS: { chave: Prateleira; titulo: string; diz: string }[] = [
  {
    chave: 'pronta', titulo: 'Prontas para descarte',
    diz: 'Laudo entregue ao cliente e prazo de guarda vencido — ou descarte '
      + 'autorizado pelo gestor ou pelo responsável técnico. Podem sair hoje.',
  },
  {
    chave: 'retida', titulo: 'Retidas a pedido do cliente',
    diz: 'Não saem, nem com o prazo vencido, enquanto a retenção vigorar '
      + '(RN-184). Soltar a retenção é ato separado, na ficha da amostra.',
  },
  {
    chave: 'entrega', titulo: 'Esperando a entrega ao cliente',
    diz: 'Laudo emitido e ainda não disponibilizado ao cliente. A contagem '
      + 'de guarda só começa na entrega — registre-a na ficha da amostra ou no Portal do cliente.',
  },
  {
    chave: 'emissao', titulo: 'Esperando o laudo ser emitido',
    diz: 'Ensaios aprovados e laudo aprovado, mas o PDF oficial ainda não saiu.',
  },
  {
    chave: 'contando', titulo: 'Prazo de guarda correndo',
    diz: 'Laudo entregue; a amostra fica guardada até a data de liberação.',
  },
];

const FILTRO_PRATELEIRA: OpcaoDeFiltro[] = PRATELEIRAS.map((p) => ({
  valor: p.chave, texto: p.titulo,
}));

export function Descarte() {
  const { pode, eu } = useSessao();
  const [linhas, setLinhas] = useState<Linha[] | null>(null);
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState('');
  const [cliente, setCliente] = useState<string | null>(null);
  const [prateleira, setPrateleira] = useState<string | null>(null);
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [baixando, setBaixando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoBaixa | null>(null);

  const carregar = useCallback(async (sinal?: AbortSignal) => {
    const q = new URLSearchParams();
    if (busca.trim()) q.set('busca', busca.trim());
    if (cliente) q.set('cliente', cliente);
    if (prateleira) q.set('prateleira', prateleira);
    const qs = q.toString();
    try {
      const [l, r] = await Promise.all([
        api<Linha[]>(`/descarte/fila${qs ? `?${qs}` : ''}`, { sinal }),
        api<Resumo>('/descarte/resumo', { sinal }),
      ]);
      setLinhas(l); setResumo(r); setErro(null);
    } catch (e) {
      if ((e as Error).name !== 'AbortError') setErro(e);
    }
  }, [busca, cliente, prateleira]);

  useEffect(() => {
    const abortar = new AbortController();
    const t = setTimeout(() => { void carregar(abortar.signal); }, busca ? 300 : 0);
    return () => { clearTimeout(t); abortar.abort(); };
  }, [carregar, busca]);

  const porPrateleira = useMemo(() => {
    const m = new Map<Prateleira, Linha[]>();
    for (const l of linhas ?? []) {
      if (!m.has(l.prateleira)) m.set(l.prateleira, []);
      m.get(l.prateleira)!.push(l);
    }
    return m;
  }, [linhas]);

  const prontas = porPrateleira.get('pronta') ?? [];
  const todasMarcadas = prontas.length > 0 && prontas.every((p) => marcadas.has(p.id));

  function alternar(id: string) {
    setMarcadas((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  function alternarTodas() {
    setMarcadas(todasMarcadas ? new Set() : new Set(prontas.map((p) => p.id)));
  }

  const podeDescartar = pode('amostra.descartar');

  return (
    <div className="descarte">
      <div className="linha-topo">
        <h1 className="cresce">Descarte de amostras</h1>
        {marcadas.size > 0 && (podeDescartar ? (
          <button type="button" className="botao" onClick={() => setBaixando(true)}>
            Dar baixa em {plural(marcadas.size, 'amostra', 'amostras')}
          </button>
        ) : (
          <AcaoBloqueada texto="Dar baixa" oQueFaz="não descarta amostra"
            permissao="amostra.descartar" persona={eu?.atuandoPor ? eu.nome : null} />
        ))}
      </div>
      <p className="sub">
        A fila é apurada pelo sistema: ensaios aprovados, laudo emitido e
        entregue ao cliente, e prazo de guarda da OS vencido. O que ainda
        não pode sair aparece abaixo, com o motivo.
      </p>

      {resumo && (
        <div className="resumo-descarte" role="list">
          <Numero valor={resumo.prontas} texto="prontas" tom={resumo.prontas > 0 ? 'ok' : 'neutro'} />
          <Numero valor={resumo.retidas} texto="retidas" tom={resumo.retidas > 0 ? 'atencao' : 'neutro'} />
          <Numero valor={resumo.aguardando_entrega} texto="esperando entrega" tom="neutro" />
          <Numero valor={resumo.contando} texto="contando" tom="neutro" />
          {resumo.vencem_em_7_dias > 0 && (
            <Numero valor={resumo.vencem_em_7_dias} texto="liberam em 7 dias" tom="neutro" />
          )}
        </div>
      )}

      <div className="linha" style={{ marginBottom: '.9rem' }}>
        <input
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Código da amostra, identificação, cliente, obra ou OS"
          aria-label="Buscar na fila de descarte"
        />
      </div>

      <FaixaDeFiltros>
        <Seletor rotulo="Prateleira" valor={prateleira} opcoes={FILTRO_PRATELEIRA}
          aoMudar={setPrateleira} todos="Todas as prateleiras" />
        <SeletorDeCliente valor={cliente} aoMudar={setCliente} />
      </FaixaDeFiltros>

      <ErroDaApi erro={erro} />

      {resultado && (
        <Aviso tipo={resultado.recusadas > 0 ? 'atencao' : 'ok'}
          titulo={`${plural(resultado.descartadas, 'amostra descartada', 'amostras descartadas')}`
            + (resultado.recusadas > 0
              ? ` · ${plural(resultado.recusadas, 'recusada', 'recusadas')} pelo banco.`
              : '.')}>
          {resultado.detalhe.recusadas.length > 0 && (
            <ul className="recusas">
              {resultado.detalhe.recusadas.map((r) => (
                <li key={r.id}>
                  {/* 303: a recusa é dita pelo nome do cliente; a rota só
                      devolve o código, o nome vem da fila carregada. */}
                  <b>{(() => {
                    const l = linhas?.find((x) => x.id === r.id);
                    return l ? nomeDaAmostra(daApi(l, true)) : r.codigo ?? r.id;
                  })()}</b> — {r.porque}
                </li>
              ))}
            </ul>
          )}
          {' '}A custódia terminou; a ficha, os ensaios, as fotos e os laudos
          continuam consultáveis.
        </Aviso>
      )}

      {linhas === null && !erro && <Carregando texto="Apurando a fila de descarte" />}

      {linhas && linhas.length === 0 && (
        <Vazio>
          {busca.trim() || cliente || prateleira
            ? 'Nenhuma amostra nesta seleção.'
            : 'Nenhuma amostra na fila de descarte. Amostra com ensaio em '
              + 'aberto não entra aqui — está na fila do laboratório.'}
        </Vazio>
      )}

      {PRATELEIRAS.map((p) => {
        const itens = porPrateleira.get(p.chave) ?? [];
        if (itens.length === 0) return null;
        const ehPronta = p.chave === 'pronta';
        return (
          <section key={p.chave} className={`cartao prateleira ${p.chave}`}>
            <h2>
              {p.titulo}
              <span className="contador">{itens.length}</span>
              {ehPronta && podeDescartar && (
                <label className="marcar-todas">
                  <input type="checkbox" checked={todasMarcadas}
                    onChange={alternarTodas}
                    aria-label="Marcar todas as amostras prontas" />
                  {' '}marcar todas
                </label>
              )}
            </h2>
            <p className="sub">{p.diz}</p>
            {/*
              * Cada prateleira é uma tabela com ficha (onda 219): a amostra,
              * de quem é e a situação ficam; onde está é detalhe e, com a
              * ficha aberta, mora nela — junto do motivo de poder (ou não)
              * sair hoje, que é a pergunta desta tela.
              */}
            <TabelaComFicha
              linhas={itens}
              chave={(l) => l.id}
              nome={(l) => `Amostra ${nomeDaAmostra(daApi(l, true))}`}
              rotulo={p.titulo}
              classeDaLinha={(l) => (marcadas.has(l.id) ? 'marcada' : undefined)}
              colunas={colunasDoDescarte({
                marcar: ehPronta && podeDescartar, marcadas, alternar,
                situacao: ehPronta ? 'Liberada desde' : 'Situação',
              })}
              ficha={fichaDoDescarte}
              lixeira={{ tipo: 'amostra', id: (l) => l.id, rotulo: (l) => `amostra ${nomeDaAmostra(daApi(l, true))}`,
                aoApagar: () => { void carregar(); } }}
            />
          </section>
        );
      })}

      {baixando && (
        <FormBaixa
          amostras={prontas.filter((p) => marcadas.has(p.id))}
          aoFechar={() => setBaixando(false)}
          aoPronto={(r) => {
            setBaixando(false);
            setResultado(r);
            setMarcadas(new Set());
            void carregar();
          }}
        />
      )}
    </div>
  );
}

function Numero({ valor, texto, tom }: { valor: number; texto: string; tom: 'ok' | 'atencao' | 'neutro' }) {
  return (
    <div className={`numero ${tom}`} role="listitem">
      <b>{valor}</b>
      <span>{texto}</span>
    </div>
  );
}

/** A coluna que diz em que pé a amostra está — uma frase, sem depender de cor. */
function Situacao({ l }: { l: Linha }) {
  switch (l.prateleira) {
    case 'pronta':
      if (l.descarte_autorizado) {
        return (
          <>
            <Selo tom="ok">Autorizada</Selo>
            <div className="sub">
              até {data(l.autorizacao_valida_ate)} · {l.autorizacao_motivo}
            </div>
          </>
        );
      }
      return (
        <>
          <b>{data(l.descarte_a_partir_de)}</b>
          <div className="sub">
            {l.dias_para_descarte !== null && l.dias_para_descarte < 0
              ? `vencida há ${plural(-l.dias_para_descarte, 'dia', 'dias')}`
              : 'libera hoje'}
            {' · '}entregue em {data(l.relatorio_disponibilizado_em)}
          </div>
        </>
      );
    case 'retida':
      return (
        <>
          <Selo tom="atencao">Retida</Selo>
          <div className="sub">
            {l.retencao_ate ? `até ${data(l.retencao_ate)}` : 'sem prazo'}
            {l.retencao_pedido_por && ` · ${l.retencao_pedido_por}`}
            {l.retencao_motivo && ` · ${l.retencao_motivo}`}
          </div>
        </>
      );
    case 'entrega':
      return (
        <>
          laudo emitido, não entregue
          <div className="sub">
            <Link to={`/amostra/${encodeURIComponent(l.codigo)}`}>
              entregar pela ficha
            </Link>
          </div>
        </>
      );
    case 'emissao':
      return <>laudo aprovado, sem PDF emitido</>;
    case 'contando':
      return (
        <>
          <b>faltam {plural(l.dias_para_descarte ?? 0, 'dia', 'dias')}</b>
          <div className="sub">
            libera em {data(l.descarte_a_partir_de)} · guarda de{' '}
            {plural(l.prazo_guarda_dias ?? 0, 'dia', 'dias')} pela OS
          </div>
        </>
      );
  }
}

/**
 * O tipo da amostra — a linha de baixo do nome. Desde a onda 303 (RN-248) a
 * identificação do cliente é o próprio nome (`<NomeDaAmostra>`), em cima.
 */
function identificacao(l: Linha): string | null {
  return l.tipo_amostra || null;
}

/** As colunas de uma prateleira do descarte (onda 219). */
function colunasDoDescarte({ marcar, marcadas, alternar, situacao }: {
  marcar: boolean; marcadas: Set<string>; alternar(id: string): void; situacao: string;
}): ColunaComFicha<Linha>[] {
  const cols: ColunaComFicha<Linha>[] = [];
  if (marcar) {
    cols.push({
      chave: 'marca',
      cabeca: <th scope="col" className="col-marca" aria-label="Marcar" />,
      celula: (l) => (
        <td className="marca">
          <input type="checkbox" checked={marcadas.has(l.id)}
            onChange={() => alternar(l.id)}
            aria-label={`Marcar ${nomeDaAmostra(daApi(l, true))}`} />
        </td>
      ),
    });
  }
  cols.push(
    {
      chave: 'amostra',
      cabeca: <th scope="col">Amostra</th>,
      celula: (l) => (
        <th scope="row">
          {/* 303 (RN-248): o nome do cliente, com o código e a OS embaixo —
              a fila de descarte mistura amostras de todas as OS. */}
          <Link to={`/amostra/${encodeURIComponent(l.codigo)}`}>
            <NomeDaAmostra amostra={daApi(l, true)} />
          </Link>
          {identificacao(l) && <span className="sub">{identificacao(l)}</span>}
        </th>
      ),
    },
    {
      chave: 'cliente',
      cabeca: <th scope="col">Cliente · obra · OS</th>,
      celula: (l) => (
        <td>
          {clienteEObra(l.cliente, l.obra_nome).join(' · ')
            || <span className="nada">sem OS</span>}
          {l.os_numero && (
            <span className="sub">
              <Link to={`/os/${encodeURIComponent(l.os_id ?? l.os_numero)}`}>OS {l.os_numero}</Link>
            </span>
          )}
        </td>
      ),
    },
    {
      chave: 'local', detalhe: true,
      cabeca: <th scope="col" className="col-opcional">Onde está</th>,
      celula: (l) => <td className="col-opcional">{l.onde_esta}</td>,
    },
    {
      chave: 'situacao',
      cabeca: <th scope="col">{situacao}</th>,
      celula: (l) => <td><Situacao l={l} /></td>,
    },
  );
  return cols;
}

/** Por que pode (ou não) sair hoje — a frase curta do aviso da ficha. */
function porQue(l: Linha): { tom: '' | 'critico'; forte: string; texto: string } {
  switch (l.prateleira) {
    case 'pronta':
      return l.descarte_autorizado
        ? { tom: '', forte: 'Pode sair hoje.', texto: `Descarte autorizado: ${l.autorizacao_motivo ?? ''}` }
        : { tom: '', forte: 'Pode sair hoje.', texto: 'Laudo entregue ao cliente e prazo de guarda vencido.' };
    case 'retida':
      return { tom: 'critico', forte: 'Retida a pedido do cliente.', texto: 'Não sai enquanto a retenção vigorar (RN-184).' };
    case 'entrega':
      return { tom: '', forte: 'Esperando a entrega.', texto: 'A contagem de guarda só começa quando o laudo chega ao cliente.' };
    case 'emissao':
      return { tom: '', forte: 'Esperando o laudo.', texto: 'Ensaios aprovados, mas o PDF oficial ainda não saiu.' };
    default:
      return { tom: '', forte: 'Prazo de guarda correndo.', texto: `Libera em ${data(l.descarte_a_partir_de)}.` };
  }
}

/** A ficha da amostra na fila de descarte. */
function fichaDoDescarte(l: Linha): FichaDaLinha {
  const q = porQue(l);
  return {
    rotulo: 'Amostra',
    titulo: <NomeDaAmostra amostra={daApi(l, true)} forte={false} />,
    sub: (
      <>
        {identificacao(l) && <span>{identificacao(l)}</span>}
        <span>{clienteEObra(l.cliente, l.obra_nome).join(' · ')}</span>
      </>
    ),
    corpo: (
      <>
        <div className={`tf-aviso${q.tom ? ` ${q.tom}` : ''}`}>
          <strong>{q.forte}</strong> {q.texto}
        </div>
        <CamposDaFicha itens={[
          ['OS', l.os_numero && <span className="mono">{l.os_numero}</span>],
          ['Onde está', l.onde_esta],
          ['Recebida em', l.datahora_recebimento && data(l.datahora_recebimento)],
          ['Laudo aprovado', l.relatorio_aprovado_em && data(l.relatorio_aprovado_em)],
          ['Laudo entregue', l.relatorio_disponibilizado_em && data(l.relatorio_disponibilizado_em)],
          ['Guarda pela OS', l.prazo_guarda_dias !== null
            ? plural(l.prazo_guarda_dias, 'dia', 'dias') : null],
          ['Libera em', l.descarte_a_partir_de && data(l.descarte_a_partir_de)],
          ['Retenção', l.retida
            ? [l.retencao_ate ? `até ${data(l.retencao_ate)}` : 'sem prazo',
              l.retencao_pedido_por, l.retencao_motivo].filter(Boolean).join(' · ')
            : null],
          ['Impedimento', l.impedimento_texto],
        ]} />
      </>
    ),
    acoes: (
      <>
        {l.os_numero && (
          <Link className="botao secundario"
            to={`/os/${encodeURIComponent(l.os_id ?? l.os_numero)}`}>Ver OS</Link>
        )}
        <Link className="botao" to={`/amostra/${encodeURIComponent(l.codigo)}`}>
          Abrir amostra
        </Link>
      </>
    ),
  };
}

/**
 * A baixa em lote.
 *
 * Justificativa obrigatória (RN-183: o lançamento é final) e destinação
 * (RN-182: para onde foi o material). Segurar para confirmar, porque
 * não há desfazer.
 */
function FormBaixa({ amostras, aoFechar, aoPronto }: {
  amostras: Linha[];
  aoFechar(): void;
  aoPronto(r: ResultadoBaixa): void;
}) {
  const [justificativa, setJustificativa] = useState('');
  const [destinacao, setDestinacao] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const pronto = justificativa.trim().length >= 5 && amostras.length > 0;

  async function confirmar() {
    if (!pronto || enviando) return;
    setEnviando(true); setErro(null);
    try {
      const r = await api<ResultadoBaixa>('/descarte/baixa', {
        metodo: 'POST',
        corpo: {
          amostraIds: amostras.map((a) => a.id),
          justificativa: justificativa.trim(),
          destinacao: destinacao.trim() || undefined,
        },
      });
      aoPronto(r);
    } catch (e) {
      setErro(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialogo aberto titulo="Dar baixa por descarte" aoFechar={aoFechar}>
      <p className="sub">
        {plural(amostras.length, 'amostra', 'amostras')}:{' '}
        {amostras.map((a) => nomeDaAmostra(daApi(a, true))).join(', ')}
      </p>

      <ErroDaApi erro={erro} />

      <Area
        rotulo="Justificativa"
        value={justificativa}
        onChange={(e) => setJustificativa(e.currentTarget.value)}
        rows={2}
        dica="Obrigatória: este lançamento não se desfaz. Vale para todas as amostras marcadas."
        required
      />

      <Campo
        rotulo="Destinação do material"
        value={destinacao}
        onChange={(e) => setDestinacao(e.currentTarget.value)}
        placeholder="Ex.: caçamba de entulho, devolvido ao solo, reaproveitado"
        dica="Para onde o material foi (RN-182). Opcional até a Testesolo definir o vocabulário."
      />

      <Aviso tipo="atencao" titulo="Este lançamento é final.">
        A amostra passa a «descartada» com data-hora do servidor, usuário e
        dispositivo. Nada se apaga: ficha, ensaios, fotos e laudos continuam.
        Retenção do cliente ou prazo correndo são recusados pelo banco, uma a
        uma — e o resultado diz quais.
      </Aviso>

      <div className="linha" style={{ marginTop: '1rem' }}>
        <button type="button" className="botao secundario" onClick={aoFechar}>
          Cancelar
        </button>
        <SegurarParaConfirmar
          texto={enviando ? 'Gravando…' : `Segure para descartar ${amostras.length}`}
          desabilitado={!pronto || enviando}
          aoConfirmar={() => void confirmar()}
        />
      </div>
    </Dialogo>
  );
}
